(() => {
  const SELECTOR = '[data-arrival-date]';
  const DATA_INITIALIZED = 'arrivalDateInitialized';
  const HOLIDAY_PROMISES = new Map();

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => callback(document));
    } else {
      callback(document);
    }
  };

  const toNumberOrNull = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  class BusinessCalendar {
    constructor(holidays = []) {
      this.holidays = new Set(holidays);
    }

    asKey(date) {
      return formatDate(date);
    }

    isHoliday(date) {
      return this.holidays.has(this.asKey(date));
    }

    isBusinessDay(date) {
      const day = date.getDay();
      return day !== 0 && day !== 6 && !this.isHoliday(date);
    }

    addBusinessDays(date, amount) {
      let remaining = Math.max(0, amount);
      const cursor = cloneDate(date);
      while (remaining > 0) {
        cursor.setDate(cursor.getDate() + 1);
        if (this.isBusinessDay(cursor)) {
          remaining -= 1;
        }
      }
      return cursor;
    }

    nextBusinessDay(date) {
      const cursor = cloneDate(date);
      do {
        cursor.setDate(cursor.getDate() + 1);
      } while (!this.isBusinessDay(cursor));
      return cursor;
    }
  }

  const parseHolidayCsv = (text) => {
    if (!text) return new Set();
    const lines = text.split(/\r?\n/);
    return lines.reduce((set, line, index) => {
      if (!line) return set;
      if (index === 0 && line.includes('祝日')) return set;
      const [rawDate] = line.split(',');
      const normalized = normalizeCsvDate(rawDate);
      if (normalized) {
        set.add(normalized);
      }
      return set;
    }, new Set());
  };

  const normalizeCsvDate = (value) => {
    if (!value) return null;
    const parts = value.trim().split(/[\/-]/);
    if (parts.length < 3) return null;
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!year || !month || !day) return null;
    return [
      year.toString().padStart(4, '0'),
      month.toString().padStart(2, '0'),
      day.toString().padStart(2, '0'),
    ].join('-');
  };

  const fetchHolidays = (url) => {
    if (!url) return Promise.resolve(new Set());
    if (HOLIDAY_PROMISES.has(url)) {
      return HOLIDAY_PROMISES.get(url);
    }
    const promise = fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load holidays: ${response.status}`);
        }
        return response.text();
      })
      .then(parseHolidayCsv)
      .catch((error) => {
        console.error('[arrival-date] holiday load error', error);
        return new Set();
      });
    HOLIDAY_PROMISES.set(url, promise);
    return promise;
  };

  const cloneDate = (date) => {
    const cloned = new Date(date.getTime());
    cloned.setHours(0, 0, 0, 0);
    return cloned;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseInputDate = (value) => {
    if (!value) return null;
    const parts = value.split('-');
    if (parts.length !== 3) return null;
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!year || !month || !day) return null;
    const date = new Date();
    date.setFullYear(year, month - 1, day);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const enforceNonBusinessRule = (date, calendar, shiftNonBusiness) => {
    let normalized = cloneDate(date);
    if (calendar.isBusinessDay(normalized)) {
      return normalized;
    }
    if (shiftNonBusiness) {
      const nextBusiness = calendar.nextBusinessDay(normalized);
      return calendar.addBusinessDays(nextBusiness, 1);
    }
    return calendar.nextBusinessDay(normalized);
  };

  const computeInitialMin = (calendar, leadDays, shiftNonBusiness, cutoffHour) => {
    const now = new Date();
    const base = cloneDate(now);
    if (Number.isFinite(cutoffHour) && cutoffHour >= 0 && cutoffHour <= 23) {
      if (now.getHours() >= cutoffHour) {
        base.setDate(base.getDate() + 1);
      }
    }
    if (calendar.isBusinessDay(base)) {
      return calendar.addBusinessDays(base, Math.max(leadDays, 1));
    }
    return enforceNonBusinessRule(base, calendar, shiftNonBusiness);
  };

  const handleInputChange = (event, calendar, minDate, shiftNonBusiness) => {
    const input = event.currentTarget;
    const selected = parseInputDate(input.value);
    if (!selected) return;
    let normalized = enforceNonBusinessRule(selected, calendar, shiftNonBusiness);
    if (normalized < minDate) {
      normalized = minDate;
    }
    const formatted = formatDate(normalized);
    if (formatted !== input.value) {
      input.value = formatted;
    }
  };

  const initInput = (input, holidays) => {
    if (input.dataset[DATA_INITIALIZED] === 'true') return;
    input.dataset[DATA_INITIALIZED] = 'true';

    const leadDays = Number(input.dataset.arrivalLeadDays || '1');
    const shiftNonBusiness = input.dataset.arrivalShiftNonbusiness !== 'false';
    const cutoffEnabled = input.dataset.arrivalCutoffEnabled !== 'false';
    const cutoffHour = cutoffEnabled ? toNumberOrNull(input.dataset.arrivalCutoffHour) : null;

    const calendar = new BusinessCalendar(Array.from(holidays));
    const minDate = computeInitialMin(calendar, leadDays, shiftNonBusiness, cutoffHour);
    const minDateString = formatDate(minDate);

    input.min = minDateString;
    if (!input.value || parseInputDate(input.value) < minDate) {
      input.value = minDateString;
    }

    input.addEventListener('change', (event) => handleInputChange(event, calendar, minDate, shiftNonBusiness));
  };

  const initScope = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    const inputs = root.querySelectorAll(SELECTOR);
    if (!inputs.length) return;
    const sample = inputs[0];
    fetchHolidays(sample.dataset.arrivalHolidaysSrc).then((holidays) => {
      inputs.forEach((input) => initInput(input, holidays));
    });
  };

  ready(initScope);
  document.addEventListener('shopify:section:load', (event) => initScope(event.target));
  document.addEventListener('shopify:section:reorder', (event) => initScope(event.target));
})();
